import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { companiesAPI, assetsAPI, serviceFormsAPI, inventoryAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { formatDate, formatDateTime, getStatusColor, getStatusLabel, getCategoryColor, getCategoryLabel, getDeviceTypeLabel, getDeviceTypeColor } from '../lib/utils';
import { toast } from 'sonner';
import { 
    ArrowLeft, Save, Loader2, AlertTriangle, Trash2, Building2, Phone, Mail, User, 
    Server, FileSignature, Network, Plus, X, Move, Circle, Square, Router, Monitor,
    Wifi, Database, Shield, Cloud, Printer, MousePointer, Laptop, HardDrive, Eye, EyeOff, Copy
} from 'lucide-react';

// Network Topology Node Types
const NODE_TYPES = [
    { id: 'router', label: 'Router', icon: Router, color: '#3b82f6' },
    { id: 'switch', label: 'Switch', icon: Network, color: '#8b5cf6' },
    { id: 'server', label: 'Server', icon: Server, color: '#ef4444' },
    { id: 'computer', label: 'Bilgisayar', icon: Monitor, color: '#22c55e' },
    { id: 'firewall', label: 'Firewall', icon: Shield, color: '#f59e0b' },
    { id: 'wifi', label: 'Access Point', icon: Wifi, color: '#06b6d4' },
    { id: 'cloud', label: 'Cloud', icon: Cloud, color: '#64748b' },
    { id: 'database', label: 'Database', icon: Database, color: '#ec4899' },
    { id: 'printer', label: 'Yazıcı', icon: Printer, color: '#84cc16' },
];

export function CompanyForm() {
    const navigate = useNavigate();
    const { id } = useParams();
    const { isAdmin } = useAuth();
    const isEdit = !!id;

    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [company, setCompany] = useState(null);
    const [assets, setAssets] = useState([]);
    const [serviceForms, setServiceForms] = useState([]);
    const [inventory, setInventory] = useState([]);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState('info');

    // Topology State
    const [nodes, setNodes] = useState([]);
    const [connections, setConnections] = useState([]);
    const [selectedNodeType, setSelectedNodeType] = useState('router');
    const [selectedNode, setSelectedNode] = useState(null);
    const [isConnecting, setIsConnecting] = useState(false);
    const [connectionStart, setConnectionStart] = useState(null);
    const [draggingNode, setDraggingNode] = useState(null);
    const [nodeDialogOpen, setNodeDialogOpen] = useState(false);
    const [newNodeLabel, setNewNodeLabel] = useState('');
    const [newNodeIP, setNewNodeIP] = useState('');
    const canvasRef = useRef(null);

    // Inventory State
    const [inventoryDialogOpen, setInventoryDialogOpen] = useState(false);
    const [editingInventory, setEditingInventory] = useState(null);
    const [savingInventory, setSavingInventory] = useState(false);
    const [showPasswords, setShowPasswords] = useState({});
    const [inventoryForm, setInventoryForm] = useState({
        device_type: 'pc',
        device_name: '',
        brand: '',
        model: '',
        serial_number: '',
        mac_address: '',
        ip_address: '',
        user_name: '',
        username: '',
        password: '',
        location: '',
        notes: '',
        quantity: 1
    });

    const [formData, setFormData] = useState({
        name: '',
        address: '',
        phone: '',
        email: '',
        tax_number: '',
        contact_person: ''
    });

    useEffect(() => {
        if (isEdit) loadData();
    }, [id]);

    const loadData = async () => {
        try {
            const [companyRes, assetsRes, formsRes, inventoryRes] = await Promise.all([
                companiesAPI.getOne(id),
                assetsAPI.getAll({ company_id: id }),
                serviceFormsAPI.getAll({ company_id: id }),
                inventoryAPI.getAll({ company_id: id })
            ]);
            
            const data = companyRes.data;
            setCompany(data);
            setAssets(assetsRes.data);
            setServiceForms(formsRes.data);
            setInventory(inventoryRes.data);
            
            setFormData({
                name: data.name,
                address: data.address || '',
                phone: data.phone || '',
                email: data.email || '',
                tax_number: data.tax_number || '',
                contact_person: data.contact_person || ''
            });

            // Load topology from localStorage
            const savedTopology = localStorage.getItem(`topology_${id}`);
            if (savedTopology) {
                const { nodes: savedNodes, connections: savedConnections } = JSON.parse(savedTopology);
                setNodes(savedNodes || []);
                setConnections(savedConnections || []);
            }
        } catch (err) {
            console.error(err);
            setError('Veri yüklenirken hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSaving(true);

        try {
            if (isEdit) {
                await companiesAPI.update(id, formData);
                toast.success('Şirket güncellendi');
            } else {
                await companiesAPI.create(formData);
                toast.success('Şirket oluşturuldu');
            }
            navigate('/companies');
        } catch (err) {
            setError(err.response?.data?.detail || 'İşlem başarısız oldu');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Bu şirketi silmek istediğinizden emin misiniz?')) return;
        
        setDeleting(true);
        try {
            await companiesAPI.delete(id);
            localStorage.removeItem(`topology_${id}`);
            toast.success('Şirket silindi');
            navigate('/companies');
        } catch (err) {
            toast.error('Silme işlemi başarısız oldu');
        } finally {
            setDeleting(false);
        }
    };

    // Topology Functions
    const saveTopology = useCallback(() => {
        if (id) {
            localStorage.setItem(`topology_${id}`, JSON.stringify({ nodes, connections }));
            toast.success('Topoloji kaydedildi');
        }
    }, [id, nodes, connections]);

    const addNode = () => {
        if (!newNodeLabel.trim()) {
            toast.error('Cihaz adı gerekli');
            return;
        }

        const nodeType = NODE_TYPES.find(t => t.id === selectedNodeType);
        const newNode = {
            id: `node_${Date.now()}`,
            type: selectedNodeType,
            label: newNodeLabel,
            ip: newNodeIP,
            x: 100 + Math.random() * 300,
            y: 100 + Math.random() * 200,
            color: nodeType?.color || '#64748b'
        };
        setNodes([...nodes, newNode]);
        setNodeDialogOpen(false);
        setNewNodeLabel('');
        setNewNodeIP('');
    };

    // Inventory Functions
    const resetInventoryForm = () => {
        setInventoryForm({
            device_type: 'pc',
            device_name: '',
            brand: '',
            model: '',
            serial_number: '',
            mac_address: '',
            ip_address: '',
            user_name: '',
            username: '',
            password: '',
            location: '',
            notes: '',
            quantity: 1
        });
        setEditingInventory(null);
    };

    const openInventoryDialog = (item = null) => {
        if (item) {
            setEditingInventory(item);
            setInventoryForm({
                device_type: item.device_type,
                device_name: item.device_name,
                brand: item.brand || '',
                model: item.model || '',
                serial_number: item.serial_number || '',
                mac_address: item.mac_address || '',
                ip_address: item.ip_address || '',
                user_name: item.user_name || '',
                username: item.username || '',
                password: item.password || '',
                location: item.location || '',
                notes: item.notes || '',
                quantity: item.quantity || 1
            });
        } else {
            resetInventoryForm();
        }
        setInventoryDialogOpen(true);
    };

    const handleSaveInventory = async () => {
        if (!inventoryForm.device_name.trim()) {
            toast.error('Cihaz adı gerekli');
            return;
        }

        setSavingInventory(true);
        try {
            const data = { ...inventoryForm, company_id: id };
            if (editingInventory) {
                await inventoryAPI.update(editingInventory.id, data);
                toast.success('Envanter güncellendi');
            } else {
                await inventoryAPI.create(data);
                toast.success('Envanter eklendi');
            }
            setInventoryDialogOpen(false);
            resetInventoryForm();
            const res = await inventoryAPI.getAll({ company_id: id });
            setInventory(res.data);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'İşlem başarısız oldu');
        } finally {
            setSavingInventory(false);
        }
    };

    const handleDeleteInventory = async (itemId) => {
        if (!window.confirm('Bu envanter kaydını silmek istediğinizden emin misiniz?')) return;
        
        try {
            await inventoryAPI.delete(itemId);
            toast.success('Envanter silindi');
            setInventory(inventory.filter(i => i.id !== itemId));
        } catch (err) {
            toast.error('Silme işlemi başarısız oldu');
        }
    };

    const togglePasswordVisibility = (itemId) => {
        setShowPasswords(prev => ({ ...prev, [itemId]: !prev[itemId] }));
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('Panoya kopyalandı');
    };

    const deleteNode = (nodeId) => {
        setNodes(nodes.filter(n => n.id !== nodeId));
        setConnections(connections.filter(c => c.from !== nodeId && c.to !== nodeId));
        setSelectedNode(null);
    };

    const handleCanvasClick = (e) => {
        if (isConnecting && connectionStart) {
            setIsConnecting(false);
            setConnectionStart(null);
        }
    };

    const handleNodeClick = (node, e) => {
        e.stopPropagation();
        
        if (isConnecting && connectionStart && connectionStart.id !== node.id) {
            // Create connection
            const exists = connections.some(
                c => (c.from === connectionStart.id && c.to === node.id) ||
                     (c.from === node.id && c.to === connectionStart.id)
            );
            if (!exists) {
                setConnections([...connections, { 
                    id: `conn_${Date.now()}`,
                    from: connectionStart.id, 
                    to: node.id 
                }]);
            }
            setIsConnecting(false);
            setConnectionStart(null);
        } else {
            setSelectedNode(node);
        }
    };

    const startConnection = (node) => {
        setIsConnecting(true);
        setConnectionStart(node);
        setSelectedNode(null);
    };

    const handleNodeDragStart = (node, e) => {
        e.stopPropagation();
        setDraggingNode(node);
    };

    const handleCanvasMouseMove = (e) => {
        if (draggingNode) {
            const rect = e.currentTarget.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;
            
            setNodes(nodes.map(n => 
                n.id === draggingNode.id ? { ...n, x, y } : n
            ));
        }
    };

    const handleCanvasMouseUp = () => {
        setDraggingNode(null);
    };

    const deleteConnection = (connId) => {
        setConnections(connections.filter(c => c.id !== connId));
    };

    const getNodeIcon = (type) => {
        const nodeType = NODE_TYPES.find(t => t.id === type);
        return nodeType?.icon || Circle;
    };

    if (!isAdmin && !isEdit) {
        navigate('/companies');
        return null;
    }

    if (loading) {
        return (
            <div className="space-y-4">
                <Skeleton className="h-10 w-32" />
                <Skeleton className="h-96 w-full" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="company-form-page">
            <div className="flex items-center justify-between">
                <Button
                    variant="ghost"
                    onClick={() => navigate('/companies')}
                    data-testid="back-btn"
                >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Geri
                </Button>
                {isEdit && company && (
                    <h1 className="font-heading text-2xl font-bold text-slate-800">{company.name}</h1>
                )}
            </div>

            {isEdit ? (
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                    <TabsList className="grid w-full grid-cols-5 mb-6">
                        <TabsTrigger value="info" className="flex items-center gap-2">
                            <Building2 className="w-4 h-4" />
                            <span className="hidden sm:inline">Bilgiler</span>
                        </TabsTrigger>
                        <TabsTrigger value="inventory" className="flex items-center gap-2">
                            <HardDrive className="w-4 h-4" />
                            <span className="hidden sm:inline">Envanter</span> ({inventory.length})
                        </TabsTrigger>
                        <TabsTrigger value="topology" className="flex items-center gap-2">
                            <Network className="w-4 h-4" />
                            <span className="hidden sm:inline">Topoloji</span>
                        </TabsTrigger>
                        <TabsTrigger value="assets" className="flex items-center gap-2">
                            <Server className="w-4 h-4" />
                            <span className="hidden sm:inline">Ürünler</span> ({assets.length})
                        </TabsTrigger>
                        <TabsTrigger value="forms" className="flex items-center gap-2">
                            <FileSignature className="w-4 h-4" />
                            <span className="hidden sm:inline">Formlar</span> ({serviceForms.length})
                        </TabsTrigger>
                    </TabsList>

                    {/* Company Info Tab */}
                    <TabsContent value="info">
                        <Card>
                            <CardHeader>
                                <CardTitle className="font-heading text-xl">Şirket Bilgileri</CardTitle>
                                <CardDescription>Şirket detaylarını görüntüleyin ve düzenleyin</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <form onSubmit={handleSubmit} className="space-y-5">
                                    {error && (
                                        <Alert variant="destructive">
                                            <AlertTriangle className="w-4 h-4" />
                                            <AlertDescription>{error}</AlertDescription>
                                        </Alert>
                                    )}

                                    <div className="space-y-2">
                                        <Label htmlFor="name">Şirket Adı *</Label>
                                        <Input
                                            id="name"
                                            value={formData.name}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            required
                                            data-testid="name-input"
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                        <div className="space-y-2">
                                            <Label htmlFor="contact_person">Yetkili Kişi</Label>
                                            <Input
                                                id="contact_person"
                                                value={formData.contact_person}
                                                onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                                                data-testid="contact-input"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="email">E-posta</Label>
                                            <Input
                                                id="email"
                                                type="email"
                                                value={formData.email}
                                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                                data-testid="email-input"
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                        <div className="space-y-2">
                                            <Label htmlFor="phone">Telefon</Label>
                                            <Input
                                                id="phone"
                                                value={formData.phone}
                                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                                data-testid="phone-input"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="tax_number">Vergi Numarası</Label>
                                            <Input
                                                id="tax_number"
                                                value={formData.tax_number}
                                                onChange={(e) => setFormData({ ...formData, tax_number: e.target.value })}
                                                data-testid="tax-input"
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="address">Adres</Label>
                                        <Textarea
                                            id="address"
                                            value={formData.address}
                                            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                            rows={3}
                                            data-testid="address-input"
                                        />
                                    </div>

                                    {company && (
                                        <div className="text-sm text-slate-500">
                                            Oluşturma: {formatDate(company.created_at)}
                                        </div>
                                    )}

                                    <div className="flex gap-3 pt-4">
                                        <Button
                                            type="submit"
                                            className="bg-orwion-green hover:bg-orwion-green-hover"
                                            disabled={saving}
                                            data-testid="submit-btn"
                                        >
                                            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                                            Güncelle
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="destructive"
                                            onClick={handleDelete}
                                            disabled={deleting}
                                            className="ml-auto"
                                            data-testid="delete-btn"
                                        >
                                            {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                                            Sil
                                        </Button>
                                    </div>
                                </form>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Inventory (KVKK) Tab */}
                    <TabsContent value="inventory">
                        <Card>
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <CardTitle className="font-heading text-xl">KVKK Envanter</CardTitle>
                                        <CardDescription>Bilişim cihazları ve kullanıcı bilgileri</CardDescription>
                                    </div>
                                    <Button
                                        className="bg-orwion-green hover:bg-orwion-green-hover"
                                        onClick={() => openInventoryDialog()}
                                        data-testid="add-inventory-btn"
                                    >
                                        <Plus className="w-4 h-4 mr-2" />
                                        Cihaz Ekle
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardContent>
                                {inventory.length > 0 ? (
                                    <div className="overflow-x-auto">
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>Cihaz</TableHead>
                                                    <TableHead>Tür</TableHead>
                                                    <TableHead>IP / MAC</TableHead>
                                                    <TableHead>Kullanıcı</TableHead>
                                                    <TableHead>PC Bilgileri</TableHead>
                                                    <TableHead>Adet</TableHead>
                                                    <TableHead className="w-24"></TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {inventory.map(item => (
                                                    <TableRow key={item.id}>
                                                        <TableCell>
                                                            <div>
                                                                <p className="font-medium">{item.device_name}</p>
                                                                <p className="text-xs text-slate-500">
                                                                    {item.brand} {item.model}
                                                                </p>
                                                                {item.serial_number && (
                                                                    <p className="text-xs text-slate-400 font-mono">{item.serial_number}</p>
                                                                )}
                                                            </div>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Badge className={getDeviceTypeColor(item.device_type)}>
                                                                {getDeviceTypeLabel(item.device_type)}
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell>
                                                            <div className="space-y-1">
                                                                {item.ip_address && (
                                                                    <div className="flex items-center gap-1">
                                                                        <span className="text-xs font-mono bg-slate-100 px-1.5 py-0.5 rounded">{item.ip_address}</span>
                                                                        <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => copyToClipboard(item.ip_address)}>
                                                                            <Copy className="h-3 w-3" />
                                                                        </Button>
                                                                    </div>
                                                                )}
                                                                {item.mac_address && (
                                                                    <div className="flex items-center gap-1">
                                                                        <span className="text-xs font-mono text-slate-500">{item.mac_address}</span>
                                                                        <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => copyToClipboard(item.mac_address)}>
                                                                            <Copy className="h-3 w-3" />
                                                                        </Button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </TableCell>
                                                        <TableCell>
                                                            {item.user_name && (
                                                                <p className="text-sm">{item.user_name}</p>
                                                            )}
                                                            {item.location && (
                                                                <p className="text-xs text-slate-500">{item.location}</p>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            <div className="space-y-1">
                                                                {item.username && (
                                                                    <div className="flex items-center gap-1">
                                                                        <span className="text-xs">Kullanıcı:</span>
                                                                        <span className="text-xs font-mono bg-blue-50 px-1.5 py-0.5 rounded">{item.username}</span>
                                                                        <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => copyToClipboard(item.username)}>
                                                                            <Copy className="h-3 w-3" />
                                                                        </Button>
                                                                    </div>
                                                                )}
                                                                {item.password && (
                                                                    <div className="flex items-center gap-1">
                                                                        <span className="text-xs">Şifre:</span>
                                                                        <span className="text-xs font-mono bg-red-50 px-1.5 py-0.5 rounded">
                                                                            {showPasswords[item.id] ? item.password : '••••••••'}
                                                                        </span>
                                                                        <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => togglePasswordVisibility(item.id)}>
                                                                            {showPasswords[item.id] ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                                                                        </Button>
                                                                        <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => copyToClipboard(item.password)}>
                                                                            <Copy className="h-3 w-3" />
                                                                        </Button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </TableCell>
                                                        <TableCell className="text-center font-medium">{item.quantity}</TableCell>
                                                        <TableCell>
                                                            <div className="flex gap-1">
                                                                <Button variant="ghost" size="icon" onClick={() => openInventoryDialog(item)}>
                                                                    <Monitor className="h-4 w-4" />
                                                                </Button>
                                                                <Button variant="ghost" size="icon" onClick={() => handleDeleteInventory(item.id)}>
                                                                    <Trash2 className="h-4 w-4 text-red-500" />
                                                                </Button>
                                                            </div>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                ) : (
                                    <div className="text-center py-12">
                                        <HardDrive className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                                        <p className="text-slate-500">Henüz envanter kaydı yok</p>
                                        <Button 
                                            className="mt-4 bg-orwion-green hover:bg-orwion-green-hover"
                                            onClick={() => openInventoryDialog()}
                                        >
                                            <Plus className="w-4 h-4 mr-2" />
                                            İlk Cihazı Ekle
                                        </Button>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Network Topology Tab */}
                    <TabsContent value="topology">
                        <Card>
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <CardTitle className="font-heading text-xl">Ağ Topolojisi</CardTitle>
                                        <CardDescription>Şirketin ağ yapısını görselleştirin</CardDescription>
                                    </div>
                                    <div className="flex gap-2">
                                        <Button
                                            variant="outline"
                                            onClick={() => setNodeDialogOpen(true)}
                                            data-testid="add-node-btn"
                                        >
                                            <Plus className="w-4 h-4 mr-2" />
                                            Cihaz Ekle
                                        </Button>
                                        <Button
                                            className="bg-orwion-green hover:bg-orwion-green-hover"
                                            onClick={saveTopology}
                                            data-testid="save-topology-btn"
                                        >
                                            <Save className="w-4 h-4 mr-2" />
                                            Kaydet
                                        </Button>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                {/* Toolbar */}
                                <div className="flex flex-wrap gap-2 mb-4 p-3 bg-slate-50 rounded-lg">
                                    <span className="text-sm text-slate-500 mr-2 self-center">Araçlar:</span>
                                    <Button
                                        variant={isConnecting ? "default" : "outline"}
                                        size="sm"
                                        onClick={() => {
                                            setIsConnecting(!isConnecting);
                                            setConnectionStart(null);
                                        }}
                                        className={isConnecting ? "bg-blue-500 hover:bg-blue-600" : ""}
                                    >
                                        <Network className="w-4 h-4 mr-1" />
                                        Bağlantı
                                    </Button>
                                    {selectedNode && (
                                        <>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => startConnection(selectedNode)}
                                            >
                                                Bağla
                                            </Button>
                                            <Button
                                                variant="destructive"
                                                size="sm"
                                                onClick={() => deleteNode(selectedNode.id)}
                                            >
                                                <Trash2 className="w-4 h-4 mr-1" />
                                                Sil
                                            </Button>
                                        </>
                                    )}
                                </div>

                                {/* Canvas */}
                                <div 
                                    ref={canvasRef}
                                    className="relative w-full h-[500px] bg-slate-100 rounded-lg border-2 border-dashed border-slate-300 overflow-hidden"
                                    onClick={handleCanvasClick}
                                    onMouseMove={handleCanvasMouseMove}
                                    onMouseUp={handleCanvasMouseUp}
                                    onMouseLeave={handleCanvasMouseUp}
                                    style={{ cursor: isConnecting ? 'crosshair' : 'default' }}
                                >
                                    {/* Grid Background */}
                                    <svg className="absolute inset-0 w-full h-full pointer-events-none">
                                        <defs>
                                            <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                                                <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#e2e8f0" strokeWidth="0.5"/>
                                            </pattern>
                                        </defs>
                                        <rect width="100%" height="100%" fill="url(#grid)" />
                                        
                                        {/* Connections */}
                                        {connections.map(conn => {
                                            const fromNode = nodes.find(n => n.id === conn.from);
                                            const toNode = nodes.find(n => n.id === conn.to);
                                            if (!fromNode || !toNode) return null;
                                            return (
                                                <g key={conn.id}>
                                                    <line
                                                        x1={fromNode.x}
                                                        y1={fromNode.y}
                                                        x2={toNode.x}
                                                        y2={toNode.y}
                                                        stroke="#64748b"
                                                        strokeWidth="2"
                                                        className="cursor-pointer hover:stroke-red-500"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (window.confirm('Bu bağlantıyı silmek istiyor musunuz?')) {
                                                                deleteConnection(conn.id);
                                                            }
                                                        }}
                                                    />
                                                </g>
                                            );
                                        })}

                                        {/* Connection preview */}
                                        {isConnecting && connectionStart && (
                                            <line
                                                x1={connectionStart.x}
                                                y1={connectionStart.y}
                                                x2={connectionStart.x + 50}
                                                y2={connectionStart.y}
                                                stroke="#3b82f6"
                                                strokeWidth="2"
                                                strokeDasharray="5,5"
                                            />
                                        )}
                                    </svg>

                                    {/* Nodes */}
                                    {nodes.map(node => {
                                        const NodeIcon = getNodeIcon(node.type);
                                        const isSelected = selectedNode?.id === node.id;
                                        return (
                                            <div
                                                key={node.id}
                                                className={`absolute flex flex-col items-center cursor-move transition-transform ${
                                                    isSelected ? 'scale-110 z-10' : ''
                                                }`}
                                                style={{ 
                                                    left: node.x - 30, 
                                                    top: node.y - 30,
                                                }}
                                                onClick={(e) => handleNodeClick(node, e)}
                                                onMouseDown={(e) => handleNodeDragStart(node, e)}
                                            >
                                                <div 
                                                    className={`w-14 h-14 rounded-xl flex items-center justify-center shadow-lg transition-all ${
                                                        isSelected ? 'ring-4 ring-blue-400' : ''
                                                    }`}
                                                    style={{ backgroundColor: node.color }}
                                                >
                                                    <NodeIcon className="w-7 h-7 text-white" />
                                                </div>
                                                <span className="mt-1 text-xs font-medium text-slate-700 bg-white px-2 py-0.5 rounded shadow-sm max-w-20 truncate">
                                                    {node.label}
                                                </span>
                                                {node.ip && (
                                                    <span className="text-[10px] text-slate-500 bg-slate-100 px-1 rounded">
                                                        {node.ip}
                                                    </span>
                                                )}
                                            </div>
                                        );
                                    })}

                                    {/* Empty State */}
                                    {nodes.length === 0 && (
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <div className="text-center text-slate-400">
                                                <Network className="w-16 h-16 mx-auto mb-4 opacity-30" />
                                                <p className="font-medium">Ağ Topolojisi Boş</p>
                                                <p className="text-sm">Cihaz eklemek için yukarıdaki butonu kullanın</p>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Legend */}
                                <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                                    <p className="text-sm font-medium text-slate-600 mb-2">Cihaz Türleri:</p>
                                    <div className="flex flex-wrap gap-3">
                                        {NODE_TYPES.map(type => {
                                            const Icon = type.icon;
                                            return (
                                                <div key={type.id} className="flex items-center gap-1.5 text-xs">
                                                    <div 
                                                        className="w-5 h-5 rounded flex items-center justify-center"
                                                        style={{ backgroundColor: type.color }}
                                                    >
                                                        <Icon className="w-3 h-3 text-white" />
                                                    </div>
                                                    <span className="text-slate-600">{type.label}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Assets Tab */}
                    <TabsContent value="assets">
                        <Card>
                            <CardHeader>
                                <CardTitle className="font-heading text-xl">Şirkete Verilen Ürünler</CardTitle>
                                <CardDescription>Bu şirkete kayıtlı tüm varlıklar</CardDescription>
                            </CardHeader>
                            <CardContent>
                                {assets.length > 0 ? (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Ürün Adı</TableHead>
                                                <TableHead>Seri No</TableHead>
                                                <TableHead>Kategori</TableHead>
                                                <TableHead>Kurulum Tarihi</TableHead>
                                                <TableHead>Garanti Bitiş</TableHead>
                                                <TableHead>Durum</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {assets.map(asset => (
                                                <TableRow key={asset.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/assets/${asset.id}`)}>
                                                    <TableCell className="font-medium">{asset.name}</TableCell>
                                                    <TableCell className="font-mono text-sm">{asset.serial_number}</TableCell>
                                                    <TableCell>
                                                        <Badge variant="outline" className={getCategoryColor(asset.category)}>
                                                            {getCategoryLabel(asset.category)}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell>{formatDate(asset.installation_date)}</TableCell>
                                                    <TableCell>{formatDate(asset.warranty_end_date)}</TableCell>
                                                    <TableCell>
                                                        <Badge className={getStatusColor(asset.status)}>
                                                            {getStatusLabel(asset.status)}
                                                        </Badge>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                ) : (
                                    <div className="text-center py-12">
                                        <Server className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                                        <p className="text-slate-500">Bu şirkete henüz ürün kaydedilmemiş</p>
                                        <Button 
                                            className="mt-4 bg-orwion-green hover:bg-orwion-green-hover"
                                            onClick={() => navigate('/assets/new')}
                                        >
                                            <Plus className="w-4 h-4 mr-2" />
                                            Ürün Ekle
                                        </Button>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Service Forms Tab */}
                    <TabsContent value="forms">
                        <Card>
                            <CardHeader>
                                <CardTitle className="font-heading text-xl">Servis Formları</CardTitle>
                                <CardDescription>Bu şirket için oluşturulan tüm servis formları</CardDescription>
                            </CardHeader>
                            <CardContent>
                                {serviceForms.length > 0 ? (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Talep</TableHead>
                                                <TableHead>Teknisyen</TableHead>
                                                <TableHead>Yapılan İşler</TableHead>
                                                <TableHead>Tarih</TableHead>
                                                <TableHead>Durum</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {serviceForms.map(form => (
                                                <TableRow key={form.id}>
                                                    <TableCell className="font-medium">{form.ticket_title}</TableCell>
                                                    <TableCell>{form.technician_name}</TableCell>
                                                    <TableCell className="max-w-xs truncate">{form.work_description}</TableCell>
                                                    <TableCell>{formatDateTime(form.created_at)}</TableCell>
                                                    <TableCell>
                                                        <Badge className={getStatusColor(form.status)}>
                                                            {getStatusLabel(form.status)}
                                                        </Badge>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                ) : (
                                    <div className="text-center py-12">
                                        <FileSignature className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                                        <p className="text-slate-500">Bu şirket için henüz servis formu oluşturulmamış</p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            ) : (
                /* New Company Form */
                <Card>
                    <CardHeader>
                        <CardTitle className="font-heading text-xl">Yeni Şirket</CardTitle>
                        <CardDescription>Yeni müşteri şirketi ekleyin</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-5">
                            {error && (
                                <Alert variant="destructive">
                                    <AlertTriangle className="w-4 h-4" />
                                    <AlertDescription>{error}</AlertDescription>
                                </Alert>
                            )}

                            <div className="space-y-2">
                                <Label htmlFor="name">Şirket Adı *</Label>
                                <Input
                                    id="name"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    required
                                    data-testid="name-input"
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <div className="space-y-2">
                                    <Label htmlFor="contact_person">Yetkili Kişi</Label>
                                    <Input
                                        id="contact_person"
                                        value={formData.contact_person}
                                        onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                                        data-testid="contact-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="email">E-posta</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        data-testid="email-input"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <div className="space-y-2">
                                    <Label htmlFor="phone">Telefon</Label>
                                    <Input
                                        id="phone"
                                        value={formData.phone}
                                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                        data-testid="phone-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="tax_number">Vergi Numarası</Label>
                                    <Input
                                        id="tax_number"
                                        value={formData.tax_number}
                                        onChange={(e) => setFormData({ ...formData, tax_number: e.target.value })}
                                        data-testid="tax-input"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="address">Adres</Label>
                                <Textarea
                                    id="address"
                                    value={formData.address}
                                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                    rows={3}
                                    data-testid="address-input"
                                />
                            </div>

                            <div className="flex gap-3 pt-4">
                                <Button
                                    type="submit"
                                    className="bg-orwion-green hover:bg-orwion-green-hover"
                                    disabled={saving}
                                    data-testid="submit-btn"
                                >
                                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                                    Oluştur
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => navigate('/companies')}
                                    data-testid="cancel-btn"
                                >
                                    İptal
                                </Button>
                            </div>
                        </form>
                    </CardContent>
                </Card>
            )}

            {/* Add Node Dialog */}
            <Dialog open={nodeDialogOpen} onOpenChange={setNodeDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Cihaz Ekle</DialogTitle>
                        <DialogDescription>Ağ topolojisine yeni bir cihaz ekleyin</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label>Cihaz Türü</Label>
                            <Select value={selectedNodeType} onValueChange={setSelectedNodeType}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {NODE_TYPES.map(type => {
                                        const Icon = type.icon;
                                        return (
                                            <SelectItem key={type.id} value={type.id}>
                                                <div className="flex items-center gap-2">
                                                    <Icon className="w-4 h-4" style={{ color: type.color }} />
                                                    {type.label}
                                                </div>
                                            </SelectItem>
                                        );
                                    })}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Cihaz Adı *</Label>
                            <Input
                                value={newNodeLabel}
                                onChange={(e) => setNewNodeLabel(e.target.value)}
                                placeholder="Örn: Ana Router"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>IP Adresi (Opsiyonel)</Label>
                            <Input
                                value={newNodeIP}
                                onChange={(e) => setNewNodeIP(e.target.value)}
                                placeholder="Örn: 192.168.1.1"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setNodeDialogOpen(false)}>
                            İptal
                        </Button>
                        <Button className="bg-orwion-green hover:bg-orwion-green-hover" onClick={addNode}>
                            <Plus className="w-4 h-4 mr-2" />
                            Ekle
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Inventory Dialog */}
            <Dialog open={inventoryDialogOpen} onOpenChange={setInventoryDialogOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editingInventory ? 'Envanter Düzenle' : 'Yeni Envanter Kaydı'}</DialogTitle>
                        <DialogDescription>KVKK envanter bilgilerini girin</DialogDescription>
                    </DialogHeader>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label>Cihaz Türü *</Label>
                            <Select value={inventoryForm.device_type} onValueChange={(v) => setInventoryForm({...inventoryForm, device_type: v})}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="pc">Masaüstü PC</SelectItem>
                                    <SelectItem value="laptop">Laptop</SelectItem>
                                    <SelectItem value="printer">Yazıcı</SelectItem>
                                    <SelectItem value="scanner">Tarayıcı</SelectItem>
                                    <SelectItem value="server">Sunucu</SelectItem>
                                    <SelectItem value="network">Ağ Cihazı</SelectItem>
                                    <SelectItem value="other">Diğer</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Cihaz Adı *</Label>
                            <Input
                                value={inventoryForm.device_name}
                                onChange={(e) => setInventoryForm({...inventoryForm, device_name: e.target.value})}
                                placeholder="Örn: Muhasebe PC"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Marka</Label>
                            <Input
                                value={inventoryForm.brand}
                                onChange={(e) => setInventoryForm({...inventoryForm, brand: e.target.value})}
                                placeholder="Örn: HP, Dell, Lenovo"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Model</Label>
                            <Input
                                value={inventoryForm.model}
                                onChange={(e) => setInventoryForm({...inventoryForm, model: e.target.value})}
                                placeholder="Model numarası"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Seri Numarası</Label>
                            <Input
                                value={inventoryForm.serial_number}
                                onChange={(e) => setInventoryForm({...inventoryForm, serial_number: e.target.value})}
                                placeholder="Seri no"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Adet</Label>
                            <Input
                                type="number"
                                min={1}
                                value={inventoryForm.quantity}
                                onChange={(e) => setInventoryForm({...inventoryForm, quantity: parseInt(e.target.value) || 1})}
                            />
                        </div>
                        <div className="col-span-full border-t pt-4 mt-2">
                            <p className="text-sm font-medium text-slate-700 mb-3">Ağ Bilgileri</p>
                        </div>
                        <div className="space-y-2">
                            <Label>IP Adresi</Label>
                            <Input
                                value={inventoryForm.ip_address}
                                onChange={(e) => setInventoryForm({...inventoryForm, ip_address: e.target.value})}
                                placeholder="192.168.1.100"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>MAC Adresi</Label>
                            <Input
                                value={inventoryForm.mac_address}
                                onChange={(e) => setInventoryForm({...inventoryForm, mac_address: e.target.value})}
                                placeholder="00:1A:2B:3C:4D:5E"
                            />
                        </div>
                        <div className="col-span-full border-t pt-4 mt-2">
                            <p className="text-sm font-medium text-slate-700 mb-3">Kullanıcı & Erişim Bilgileri</p>
                        </div>
                        <div className="space-y-2">
                            <Label>Kullanıcı Adı Soyadı</Label>
                            <Input
                                value={inventoryForm.user_name}
                                onChange={(e) => setInventoryForm({...inventoryForm, user_name: e.target.value})}
                                placeholder="Cihazı kullanan kişi"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Konum</Label>
                            <Input
                                value={inventoryForm.location}
                                onChange={(e) => setInventoryForm({...inventoryForm, location: e.target.value})}
                                placeholder="Oda/Departman"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>PC Kullanıcı Adı</Label>
                            <Input
                                value={inventoryForm.username}
                                onChange={(e) => setInventoryForm({...inventoryForm, username: e.target.value})}
                                placeholder="Windows kullanıcı adı"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>PC Şifresi</Label>
                            <Input
                                type="password"
                                value={inventoryForm.password}
                                onChange={(e) => setInventoryForm({...inventoryForm, password: e.target.value})}
                                placeholder="••••••••"
                            />
                        </div>
                        <div className="col-span-full space-y-2">
                            <Label>Notlar</Label>
                            <Textarea
                                value={inventoryForm.notes}
                                onChange={(e) => setInventoryForm({...inventoryForm, notes: e.target.value})}
                                placeholder="Ek notlar..."
                                rows={2}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setInventoryDialogOpen(false)}>
                            İptal
                        </Button>
                        <Button 
                            className="bg-orwion-green hover:bg-orwion-green-hover" 
                            onClick={handleSaveInventory}
                            disabled={savingInventory}
                        >
                            {savingInventory ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                            {editingInventory ? 'Güncelle' : 'Kaydet'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default CompanyForm;
